import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, Calendar, TrendingUp, Download, Search, Filter, X, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

const AdminCRM = () => {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [calculatorLeads, setCalculatorLeads] = useState([]);
  const [activeTab, setActiveTab] = useState('contacts'); // contacts, appointments, or calculator
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isSendingFollowup, setIsSendingFollowup] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Status options for Contacts (mapping backend English values to French labels)
  const contactStatuses = [
    { value: 'new', label: '🆕 Nouveau', color: 'bg-blue-100 text-blue-800' },
    { value: 'contacted', label: '📞 Contacté', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'qualified', label: '✅ Qualifié', color: 'bg-purple-100 text-purple-800' },
    { value: 'won', label: '🎉 Gagné', color: 'bg-green-100 text-green-800' },
    { value: 'lost', label: '❌ Perdu', color: 'bg-red-100 text-red-800' }
  ];

  // Status options for Appointments (mapping backend English values to French labels)
  const appointmentStatuses = [
    { value: 'pending', label: '⏳ En attente', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'confirmed', label: '✅ Confirmé', color: 'bg-blue-100 text-blue-800' },
    { value: 'completed', label: '🎉 Complété', color: 'bg-green-100 text-green-800' },
    { value: 'cancelled', label: '❌ Annulé', color: 'bg-red-100 text-red-800' },
    { value: 'no_show', label: '👻 Absent', color: 'bg-gray-100 text-gray-800' }
  ];

  const motifPerteOptions = [
    { value: 'trop_cher', label: 'Trop cher' },
    { value: 'delai', label: 'Délai trop long' },
    { value: 'autre_prestataire', label: 'Choisi autre prestataire' },
    { value: 'pas_de_reponse', label: 'Pas de réponse' },
    { value: 'projet_annule', label: 'Projet annulé' },
    { value: 'autre', label: 'Autre' }
  ];

  const calculatorStatuses = [
    { value: 'Nouveau', label: '🆕 Nouveau', color: 'bg-blue-100 text-blue-800' },
    { value: 'Contacté', label: '📞 Contacté', color: 'bg-purple-100 text-purple-800' },
    { value: 'Qualifié', label: '✅ Qualifié', color: 'bg-orange-100 text-orange-800' },
    { value: 'Gagné', label: '🎉 Gagné', color: 'bg-green-100 text-green-800' },
    { value: 'Perdu', label: '❌ Perdu', color: 'bg-red-100 text-red-800' }
  ];

  // Initial data fetch on mount
  useEffect(() => {
    fetchDashboardStats();
    fetchContacts();
    fetchAppointments();
    fetchCalculatorLeads();
  }, []);

  // Refetch when filters change or tab changes
  useEffect(() => {
    setSelectedLeads([]); // Clear selection when changing tabs
    if (activeTab === 'contacts') {
      fetchContacts();
    } else if (activeTab === 'appointments') {
      fetchAppointments();
    } else if (activeTab === 'calculator') {
      fetchCalculatorLeads();
    }
  }, [activeTab, statusFilter, searchTerm]);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`);
      setStats(response.data || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({});
    }
  };

  const fetchContacts = async () => {
    try {
      setIsLoading(true);
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/contacts`, { params });
      setContacts(response.data?.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      setIsLoading(true);
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/appointments`, { params });
      setAppointments(response.data?.appointments || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCalculatorLeads = async () => {
    try {
      setIsLoading(true);
      const params = { days: 365, limit: 1000 }; // Get all leads from last year
      if (statusFilter && statusFilter !== 'all') params.admin_status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      
      const response = await axios.get(`${BACKEND_URL}/api/devis/admin/details`, { params });
      // Get all leads (filter out Abandons only)
      const leads = (response.data?.data || []).filter(item => item.status !== 'Abandon');
      setCalculatorLeads(leads);
    } catch (error) {
      console.error('Error fetching calculator leads:', error);
      setCalculatorLeads([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportGDPR = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/export/gdpr-csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gdpr_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting GDPR CSV:', error);
      alert('Erreur lors de l\'export CSV');
    }
  };

  const openItemModal = (item, type) => {
    setSelectedItem({ ...item, type });
    setEditNotes(type === 'contact' ? (item.notes || '') : (item.admin_notes || ''));
    setEditStatus(type === 'calculator' ? (item.admin_status || 'Nouveau') : item.status);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setEditNotes('');
    setEditStatus('');
  };

  const handleSaveChanges = async () => {
    if (!selectedItem) return;

    try {
      let endpoint, updateData;
      
      if (selectedItem.type === 'contact') {
        endpoint = `${BACKEND_URL}/api/admin/contacts/${selectedItem.id}`;
        updateData = {
          status: editStatus,
          notes: editNotes
        };
      } else if (selectedItem.type === 'appointment') {
        endpoint = `${BACKEND_URL}/api/admin/appointments/${selectedItem.id}`;
        updateData = {
          status: editStatus,
          admin_notes: editNotes
        };
      } else if (selectedItem.type === 'calculator') {
        // Use MongoDB ObjectId as identifier for calculator leads
        endpoint = `${BACKEND_URL}/api/devis/admin/calculator-leads/${selectedItem.id}`;
        updateData = {
          admin_status: editStatus,
          admin_notes: editNotes
        };
      }

      await axios.put(endpoint, updateData);
      
      // Refresh data
      if (selectedItem.type === 'contact') {
        fetchContacts();
      } else if (selectedItem.type === 'appointment') {
        fetchAppointments();
      } else if (selectedItem.type === 'calculator') {
        fetchCalculatorLeads();
      }
      
      fetchDashboardStats();
      closeModal();
      alert('Modifications enregistrées avec succès!');
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const toggleSelectAll = () => {
    const currentList = activeTab === 'contacts' ? contacts : activeTab === 'appointments' ? appointments : calculatorLeads;
    if (selectedLeads.length === currentList.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(currentList.map(item => item.id));
    }
  };

  const handleSendFollowup = () => {
    console.log('=== handleSendFollowup called ===');
    console.log('selectedLeads:', selectedLeads);
    console.log('activeTab:', activeTab);
    
    if (selectedLeads.length === 0) {
      alert('Veuillez sélectionner au moins un lead');
      return;
    }

    // Show custom confirmation dialog
    setShowConfirmDialog(true);
  };

  const confirmSendFollowup = async () => {
    console.log('User confirmed, sending request...');
    setShowConfirmDialog(false);

    try {
      setIsSendingFollowup(true);
      
      // Map frontend tab names to backend lead types
      const leadTypeMap = {
        'contacts': 'contacts',
        'appointments': 'appointments',
        'calculator': 'devis'
      };
      const leadType = leadTypeMap[activeTab] || activeTab;
      
      console.log('Sending POST to:', `${BACKEND_URL}/api/admin/send-followup?lead_type=${leadType}`);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/send-followup?lead_type=${leadType}`,
        selectedLeads,
        { headers: { 'Content-Type': 'application/json' } }
      );

      console.log('Response received:', response.data);

      if (response.data.success) {
        const { sent = 0, failed = 0, total = 0, results = [] } = response.data;
        const failedItems = results.filter((r) => !r.success);

        // Build per-lead error breakdown (max 10 lines, then "+N more")
        const errorLines = failedItems.slice(0, 10).map((r) => {
          const who = r.email || r.id || 'lead inconnu';
          const why = r.error || 'erreur inconnue';
          return `• ${who} — ${why}`;
        });
        if (failedItems.length > 10) {
          errorLines.push(`… +${failedItems.length - 10} autres échecs`);
        }

        // Distinct messaging based on outcome
        let header;
        if (sent === total && total > 0) {
          header = `✅ Tous les follow-ups envoyés (${sent}/${total})`;
        } else if (sent > 0 && failed > 0) {
          header = `⚠️ Envoi partiel: ${sent}/${total} envoyés, ${failed} échec(s)`;
        } else {
          header = `❌ Aucun follow-up envoyé (${failed}/${total} échec(s))`;
        }

        const body = errorLines.length
          ? `\n\nDétails des échecs:\n${errorLines.join('\n')}`
          : '';

        alert(`${header}${body}`);
        setSelectedLeads([]);
        // Refresh data
        if (activeTab === 'contacts') {
          fetchContacts();
        } else {
          fetchAppointments();
        }
      } else {
        alert('⚠️ Une erreur s\'est produite');
      }
    } catch (error) {
      console.error('Error sending follow-up:', error);
      console.error('Error details:', error.response?.data);
      
      // Format error message properly
      let errorMessage = 'Une erreur inconnue s\'est produite';
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`❌ Erreur lors de l'envoi des follow-ups: ${errorMessage}`);
    } finally {
      setIsSendingFollowup(false);
      console.log('=== handleSendFollowup completed ===');
    }
  };

  const getFollowupBadge = (lead) => {
    const status = lead?.last_followup_status;
    if (!status) return null;
    if (status === 'sent') {
      const when = lead.last_followup_sent
        ? new Date(lead.last_followup_sent).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
        : '';
      const count = lead.followup_count || 1;
      return (
        <span
          title={`Follow-up envoyé${when ? ' le ' + when : ''}${count > 1 ? ` (${count}× au total)` : ''}`}
          className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-500/20 text-green-300 border border-green-500/40"
          data-testid="followup-badge-sent"
        >
          ✓ Envoyé{count > 1 ? ` ×${count}` : ''}
        </span>
      );
    }
    if (status === 'failed') {
      const err = lead.last_followup_error || 'Erreur inconnue';
      return (
        <span
          title={`Échec envoi follow-up: ${err}`}
          className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/20 text-red-300 border border-red-500/40 cursor-help"
          data-testid="followup-badge-failed"
        >
          ✗ Échec
        </span>
      );
    }
    return null;
  };

  const getStatusBadge = (status, type) => {
    // Map for old French status values to new English backend values
    const statusMapping = {
      'nouveau': 'new',
      'a_contacter': 'contacted',
      'contacte': 'contacted',
      'qualifie': 'qualified',
      'gagne': 'won',
      'perdu': 'lost',
      'en_attente': 'pending',
      'confirme': 'confirmed',
      'complete': 'completed',
      'annule': 'cancelled'
    };
    
    // Map old status to new if it exists
    const mappedStatus = statusMapping[status?.toLowerCase()] || status;
    
    let statuses;
    if (type === 'contact') {
      statuses = contactStatuses;
    } else if (type === 'appointment') {
      statuses = appointmentStatuses;
    } else if (type === 'calculator') {
      statuses = calculatorStatuses;
    }
    
    const statusInfo = statuses?.find(s => s.value === mappedStatus);
    return statusInfo ? (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    ) : <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">{status}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-slate-100">
      {/* Header */}
      <div className="bg-slate-950 py-6 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
        <div className="container mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              Retour Dashboard
            </Link>
            <Link to="/admin/gallery" className="text-slate-400 hover:text-slate-200 transition-colors text-sm">
              📸 Galerie
            </Link>
            <Link to="/admin/groups" className="text-slate-400 hover:text-slate-200 transition-colors text-sm">
              📁 Groupes
            </Link>
          </div>
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-terracotta-400 to-clay-400">
              CRM Admin Panel
            </h1>
            <Button 
              onClick={handleExportGDPR}
              className="bg-terracotta-600 hover:bg-terracotta-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Export GDPR CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Stats */}
        {stats && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-slate-200">
                  <Users className="w-5 h-5 text-terracotta-400" />
                  Contacts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-terracotta-400 mb-4">{stats.contacts.total}</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>🆕 Nouveaux:</span>
                    <span className="font-semibold text-slate-200">{stats.contacts.new}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>📞 Contactés:</span>
                    <span className="font-semibold text-slate-200">{stats.contacts.contacted}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>🎉 Gagnés:</span>
                    <span className="font-semibold text-green-400">{stats.contacts.won}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-slate-200">
                  <Calendar className="w-5 h-5 text-clay-400" />
                  Rendez-vous
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-clay-400 mb-4">{stats.appointments.total}</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>⏳ En attente:</span>
                    <span className="font-semibold text-slate-200">{stats.appointments.pending}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>✅ Confirmés:</span>
                    <span className="font-semibold text-slate-200">{stats.appointments.confirmed}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>🎉 Complétés:</span>
                    <span className="font-semibold text-green-400">{stats.appointments.completed}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-slate-200">
                  <TrendingUp className="w-5 h-5 text-teal-400" />
                  7 Derniers Jours
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-slate-400 mb-1">Nouveaux contacts</div>
                    <div className="text-2xl font-bold text-terracotta-400">{stats.contacts.recent_7days}</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-400 mb-1">Nouveaux RDV</div>
                    <div className="text-2xl font-bold text-clay-400">{stats.appointments.recent_7days}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('contacts')}
            className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              activeTab === 'contacts' 
                ? '!bg-[#C97249] !text-white border-2 !border-[#C97249] hover:!bg-[#B86239]' 
                : 'border-2 border-slate-600 text-slate-300 bg-transparent hover:bg-slate-800'
            }`}
          >
            <Users className="w-4 h-4 mr-2" />
            Contacts ({stats?.contacts.total || 0})
          </button>
          <button
            onClick={() => setActiveTab('appointments')}
            className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              activeTab === 'appointments' 
                ? '!bg-[#C97249] !text-white border-2 !border-[#C97249] hover:!bg-[#B86239]' 
                : 'border-2 border-slate-600 text-slate-300 bg-transparent hover:bg-slate-800'
            }`}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Rendez-vous ({stats?.appointments.total || 0})
          </button>
          <button
            onClick={() => setActiveTab('calculator')}
            className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              activeTab === 'calculator' 
                ? '!bg-[#C97249] !text-white border-2 !border-[#C97249] hover:!bg-[#B86239]' 
                : 'border-2 border-slate-600 text-slate-300 bg-transparent hover:bg-slate-800'
            }`}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Leads Calculateur ({calculatorLeads.length})
          </button>
        </div>

        {/* Action Buttons */}
        {selectedLeads.length > 0 && (
          <div className="mb-4 p-4 bg-blue-500/10 border border-blue-400/30 rounded-lg flex items-center justify-between">
            <div className="text-blue-300">
              <span className="font-semibold">{selectedLeads.length}</span> lead(s) sélectionné(s)
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSendFollowup}
                disabled={isSendingFollowup}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
              >
                {isSendingFollowup ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    📧 Envoyer Follow-up ({selectedLeads.length})
                  </>
                )}
              </button>
              <button
                onClick={() => setSelectedLeads([])}
                className="px-6 py-3 border-2 border-slate-600 text-slate-300 hover:bg-slate-700 font-semibold rounded-lg transition-all duration-200"
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300 mb-2 block">Recherche</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Nom, email, téléphone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-900 border-slate-600 text-slate-200"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300 mb-2 block">Filtre par status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-slate-200">
                    <SelectValue placeholder="Tous les status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {(activeTab === 'contacts' ? contactStatuses : activeTab === 'appointments' ? appointmentStatuses : calculatorStatuses).map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('');
                  }}
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <X className="w-4 h-4 mr-2" />
                  Réinitialiser
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="text-center py-12 text-slate-400">Chargement...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-center py-3 px-4 text-slate-300 font-semibold w-12">
                        <input
                          type="checkbox"
                          checked={selectedLeads.length === (activeTab === 'contacts' ? contacts : appointments).length && selectedLeads.length > 0}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 rounded border-slate-600 text-blue-600 focus:ring-2 focus:ring-blue-400 cursor-pointer"
                        />
                      </th>
                      <th className="text-left py-3 px-4 text-slate-300 font-semibold">Nom</th>
                      <th className="text-left py-3 px-4 text-slate-300 font-semibold">Email</th>
                      <th className="text-left py-3 px-4 text-slate-300 font-semibold">Téléphone</th>
                      <th className="text-left py-3 px-4 text-slate-300 font-semibold">Ville</th>
                      <th className="text-left py-3 px-4 text-slate-300 font-semibold">{activeTab === 'calculator' ? 'Projet' : 'Service'}</th>
                      {activeTab === 'appointments' && <th className="text-left py-3 px-4 text-slate-300 font-semibold">Date/Heure</th>}
                      <th className="text-left py-3 px-4 text-slate-300 font-semibold">Status</th>
                      <th className="text-left py-3 px-4 text-slate-300 font-semibold">Source</th>
                      <th className="text-left py-3 px-4 text-slate-300 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeTab === 'contacts' && contacts.map((contact) => (
                      <tr key={contact.id} className="border-b border-slate-700 hover:bg-slate-750 transition-colors">
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedLeads.includes(contact.id)}
                            onChange={() => toggleLeadSelection(contact.id)}
                            className="w-4 h-4 rounded border-slate-600 text-blue-600 focus:ring-2 focus:ring-blue-400 cursor-pointer"
                          />
                        </td>
                        <td className="py-3 px-4 text-slate-200">{contact.nom} {contact.prenom}</td>
                        <td className="py-3 px-4 text-slate-300">{contact.email}</td>
                        <td className="py-3 px-4 text-slate-300">{contact.telephone}</td>
                        <td className="py-3 px-4 text-slate-300">{contact.ville || 'N/A'}</td>
                        <td className="py-3 px-4 text-slate-300">{contact.service}</td>
                        <td className="py-3 px-4">{getStatusBadge(contact.status, 'contact')}{getFollowupBadge(contact)}</td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            📝 {contact.source || 'Formulaire Contact'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            size="sm"
                            onClick={() => openItemModal(contact, 'contact')}
                            className="bg-terracotta-600 hover:bg-terracotta-700"
                          >
                            Gérer
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {activeTab === 'appointments' && appointments.map((appointment) => (
                      <tr key={appointment.id} className="border-b border-slate-700 hover:bg-slate-750 transition-colors">
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedLeads.includes(appointment.id)}
                            onChange={() => toggleLeadSelection(appointment.id)}
                            className="w-4 h-4 rounded border-slate-600 text-blue-600 focus:ring-2 focus:ring-blue-400 cursor-pointer"
                          />
                        </td>
                        <td className="py-3 px-4 text-slate-200">{appointment.nom} {appointment.prenom}</td>
                        <td className="py-3 px-4 text-slate-300">{appointment.email}</td>
                        <td className="py-3 px-4 text-slate-300">{appointment.telephone}</td>
                        <td className="py-3 px-4 text-slate-300">{appointment.ville || 'N/A'}</td>
                        <td className="py-3 px-4 text-slate-300">{appointment.service}</td>
                        <td className="py-3 px-4 text-slate-300">{appointment.date} à {appointment.time}</td>
                        <td className="py-3 px-4">{getStatusBadge(appointment.status, 'appointment')}{getFollowupBadge(appointment)}</td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            📅 {appointment.source || 'Formulaire RDV'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            size="sm"
                            onClick={() => openItemModal(appointment, 'appointment')}
                            className="bg-clay-600 hover:bg-clay-700"
                          >
                            Gérer
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {activeTab === 'calculator' && calculatorLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-slate-700 hover:bg-slate-750 transition-colors">
                        <td className="py-3 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={selectedLeads.includes(lead.id)}
                            onChange={() => toggleLeadSelection(lead.id)}
                            className="w-4 h-4 rounded border-slate-600 text-blue-600 focus:ring-2 focus:ring-blue-400 cursor-pointer"
                          />
                        </td>
                        <td className="py-3 px-4 text-slate-200">{[lead.prenom, lead.nom].filter(Boolean).join(' ') || lead.email?.split('@')[0] || 'N/A'}</td>
                        <td className="py-3 px-4 text-slate-300">{lead.email}</td>
                        <td className="py-3 px-4 text-slate-300">{lead.telephone || 'N/A'}</td>
                        <td className="py-3 px-4 text-slate-300">{lead.ville || 'N/A'}</td>
                        <td className="py-3 px-4 text-slate-300">{lead.type_beton} - {lead.surface}m²</td>
                        <td className="py-3 px-4">{getStatusBadge(lead.admin_status || 'Nouveau', 'calculator')}{getFollowupBadge(lead)}</td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Calculateur Béton
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            size="sm"
                            onClick={() => openItemModal({...lead, type: 'calculator', nom: [lead.prenom, lead.nom].filter(Boolean).join(' ') || lead.email?.split('@')[0], prenom: lead.prenom || '', telephone: lead.telephone || 'N/A', service: `${lead.type_beton} - ${lead.surface}m²`, status: lead.admin_status || 'Nouveau'}, 'calculator')}
                            className="bg-[#C97249] hover:bg-[#B86239]"
                          >
                            Gérer
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {((activeTab === 'contacts' && contacts.length === 0) || (activeTab === 'appointments' && appointments.length === 0) || (activeTab === 'calculator' && calculatorLeads.length === 0)) && (
                  <div className="text-center py-12 text-slate-400">
                    Aucun résultat trouvé
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal for editing */}
      {isModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-terracotta-400 to-clay-400">
                  {selectedItem.type === 'contact' ? 'Détails Contact' : selectedItem.type === 'appointment' ? 'Détails Rendez-vous' : 'Détails Lead Calculator'}
                </h2>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-200">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Item Details */}
              <div className="space-y-4 mb-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-sm">Nom complet</Label>
                    <div className="text-slate-200 font-semibold">{selectedItem.nom} {selectedItem.prenom}</div>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Email</Label>
                    <div className="text-slate-200">{selectedItem.email}</div>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Téléphone</Label>
                    <div className="text-slate-200">{selectedItem.telephone}</div>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm">Service</Label>
                    <div className="text-slate-200">{selectedItem.service}</div>
                  </div>
                  {selectedItem.type === 'contact' && selectedItem.ville && (
                    <div>
                      <Label className="text-slate-400 text-sm">Ville</Label>
                      <div className="text-slate-200">{selectedItem.ville}</div>
                    </div>
                  )}
                  {selectedItem.type === 'appointment' && (
                    <>
                      <div>
                        <Label className="text-slate-400 text-sm">Date</Label>
                        <div className="text-slate-200">{selectedItem.date}</div>
                      </div>
                      <div>
                        <Label className="text-slate-400 text-sm">Heure</Label>
                        <div className="text-slate-200">{selectedItem.time}</div>
                      </div>
                    </>
                  )}
                </div>

                {selectedItem.message && (
                  <div>
                    <Label className="text-slate-400 text-sm">Message client</Label>
                    <div className="text-slate-200 bg-slate-900 p-3 rounded-lg">{selectedItem.message}</div>
                  </div>
                )}
              </div>

              {/* Edit Fields */}
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300 mb-2 block">Status</Label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(selectedItem.type === 'contact' ? contactStatuses : selectedItem.type === 'appointment' ? appointmentStatuses : calculatorStatuses).map(status => (
                        <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-slate-300 mb-2 block">Notes internes</Label>
                  <Textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    rows={5}
                    className="bg-slate-900 border-slate-600 text-slate-200"
                    placeholder="Ajoutez des notes sur ce lead..."
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleSaveChanges}
                  className="flex-1 bg-terracotta-600 hover:bg-terracotta-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
                <Button
                  onClick={closeModal}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  Annuler
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-2xl text-transparent bg-clip-text bg-gradient-to-r from-terracotta-400 to-clay-400">
              Confirmer l'envoi
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-base mt-2">
              Êtes-vous sûr de vouloir envoyer un email de follow-up à <span className="font-bold text-terracotta-400">{selectedLeads.length}</span> lead(s) sélectionné(s) ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmSendFollowup}
              disabled={isSendingFollowup}
              className="bg-terracotta-600 hover:bg-terracotta-700 text-white"
            >
              {isSendingFollowup ? 'Envoi en cours...' : 'Confirmer et Envoyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCRM;
